import { RewardLogsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as RewardLogsActions from './reward-logs.actions';

@Injectable()
export class RewardLogsEffects {
  private actions$ = inject(Actions);
  private rewardLogsApi = inject(RewardLogsApiService);
  private notification = inject(NotificationService);

  loadRewardLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardLogsActions.loadRewardLogs),
      switchMap(({ params }) =>
        this.rewardLogsApi.findAll(params).pipe(
          map(response => RewardLogsActions.loadRewardLogsSuccess({
            rewardLogs: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.load_reward_logs_error');
            }
            return of(RewardLogsActions.loadRewardLogsFailure({ error: errorMessage || 'Failed to load reward logs' }));
          }),
        ),
      ),
    ),
  );

  createRewardLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardLogsActions.createRewardLog),
      switchMap(({ rewardLogData }) =>
        this.rewardLogsApi.create(rewardLogData).pipe(
          map((rewardLog) => {
            this.notification.success('admin.notifications.reward_log_created');
            return RewardLogsActions.createRewardLogSuccess({ rewardLog });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.create_reward_log_error');
            }
            return of(RewardLogsActions.createRewardLogFailure({ error: errorMessage || 'Failed to create reward log' }));
          }),
        ),
      ),
    ),
  );

  deleteRewardLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardLogsActions.deleteRewardLog),
      switchMap(({ id }) =>
        this.rewardLogsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.reward_log_deleted');
            return RewardLogsActions.deleteRewardLogSuccess({ id });
          }),
          catchError((error: any) => {
            const errorMessage = error?.error?.message;
            if (errorMessage) {
              this.notification.errorRaw(errorMessage);
            } else {
              this.notification.error('admin.notifications.delete_reward_log_error');
            }
            return of(RewardLogsActions.deleteRewardLogFailure({ error: errorMessage || 'Failed to delete reward log' }));
          }),
        ),
      ),
    ),
  );
}

